import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { adminAction } from "@/lib/adminApi";
import { successToast, errorToast } from "@/components/ui/sonner";
import {
  AdminPageHeader,
  AdminPageSkeleton,
  AdminPanel,
  AdminEmptyState,
  AdminSection,
  AdminPill,
} from "./AdminUI";
import { Flag } from "lucide-react";

const FLAG_META: Record<string, { label: string; description: string }> = {
  early_access: { label: "Early access", description: "Access to features before general release" },
  beta_features: { label: "Beta features", description: "Experimental features that may change" },
  unlimited_proposals: { label: "Unlimited proposals", description: "Remove monthly proposal creation limit" },
};

const DEFAULT_FLAGS = ["early_access", "beta_features", "unlimited_proposals"];

export function AdminFeatureFlagsTab() {
  const queryClient = useQueryClient();
  const [selectedOrg, setSelectedOrg] = useState<string>("");

  const { data: orgs } = useQuery({
    queryKey: ["admin", "organizations"],
    queryFn: () => adminAction("organizations"),
  });

  const { data: flags, isLoading } = useQuery({
    queryKey: ["admin", "feature_flags"],
    queryFn: () => adminAction("feature_flags_list"),
  });

  const toggle = useMutation({
    mutationFn: (params: { org_id: string; flag_name: string; enabled: boolean }) =>
      adminAction("feature_flags_toggle", params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "feature_flags"] });
      successToast("Flag updated", "Feature flag has been toggled.");
    },
    onError: (e: Error) => errorToast("Error", e.message),
  });

  const getFlagState = (orgId: string, flagName: string): boolean => {
    return (flags || []).some((f: any) => f.organization_id === orgId && f.flag_name === flagName && f.enabled);
  };

  if (isLoading) return <AdminPageSkeleton stats={0} rows={3} columns={2} />;

  const selectedOrgName = (orgs || []).find((o: any) => o.id === selectedOrg)?.name;
  const enabledCount = DEFAULT_FLAGS.filter(f => selectedOrg && getFlagState(selectedOrg, f)).length;

  return (
    <div className="space-y-10">
      <AdminPageHeader
        eyebrow="Platform"
        title="Feature flags"
        description="Grant individual organisations early access to features that haven't shipped to the wider platform."
        meta={`${DEFAULT_FLAGS.length} flags · ${(orgs || []).length} organisations`}
      />

      <AdminSection
        title="Select organisation"
        description="Each organisation has its own flag state. Changes apply instantly."
      >
        <div className="max-w-md">
          <Select value={selectedOrg} onValueChange={setSelectedOrg}>
            <SelectTrigger
              className="h-10 text-[13px] rounded-[3px]"
              style={{
                background: "#faf7f1",
                border: "1px solid rgba(58,53,48,0.14)",
              }}
            >
              <SelectValue placeholder="Choose an organisation…" />
            </SelectTrigger>
            <SelectContent>
              {(orgs || []).map((o: any) => (
                <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </AdminSection>

      {selectedOrg ? (
        <AdminSection
          title="Available flags"
          description={selectedOrgName ? `Editing ${selectedOrgName}` : undefined}
          action={
            <AdminPill tone={enabledCount > 0 ? "accent" : "neutral"}>
              {enabledCount} / {DEFAULT_FLAGS.length} enabled
            </AdminPill>
          }
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {DEFAULT_FLAGS.map((flag) => {
              const meta = FLAG_META[flag] || { label: flag, description: "" };
              const enabled = getFlagState(selectedOrg, flag);
              return (
                <AdminPanel key={flag} padded>
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="text-[10px] uppercase tracking-[0.26em] text-muted-foreground mb-2">
                        Flag
                      </div>
                      <h3
                        className="text-[18px] text-foreground"
                        style={{
                          fontFamily: "Outfit, sans-serif",
                          fontWeight: 300,
                          letterSpacing: "-0.01em",
                        }}
                      >
                        {meta.label}
                      </h3>
                      <p className="text-[12.5px] text-muted-foreground mt-2 leading-relaxed">
                        {meta.description}
                      </p>
                    </div>
                    <Switch
                      checked={enabled}
                      onCheckedChange={(checked) =>
                        toggle.mutate({ org_id: selectedOrg, flag_name: flag, enabled: checked })
                      }
                    />
                  </div>
                </AdminPanel>
              );
            })}
          </div>
        </AdminSection>
      ) : (
        <AdminEmptyState
          icon={Flag}
          title="No organisation selected"
          description="Pick an organisation above to view and toggle their feature flags."
        />
      )}
    </div>
  );
}
